import React, { useState } from 'react';
import axios from 'axios';
import {
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Alert,
  Snackbar,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  LockOutlined as LockIcon,
  Visibility,
  VisibilityOff,
  Diamond as DiamondIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import baseURL from '../../baseURL';

const Login = () => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setOpenSnackbar(false);
    setLoading(true);

    try {
      const response = await axios.post(
        `${baseURL}/auth/login`,
        { password },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000,
        }
      );

      const { token, loginMode, accessLevel } = response.data;
      if (!token) throw new Error('No authentication token received');

      // Legal password → legal screen. Black password → black screen. Admin password → admin screen.
      const isBlack = loginMode === 'secondary' || accessLevel === 'black';
      const isAdmin = accessLevel === 'admin';
      localStorage.setItem('token', token);
      localStorage.setItem('loginMode', isBlack ? 'secondary' : 'primary');
      if (accessLevel) localStorage.setItem('userType', accessLevel);
      if (isAdmin) navigate('/admin-dashboard');
      else if (isBlack) navigate('/black');
      else navigate('/');
    } catch (err) {
      let errorMessage = 'An unexpected error occurred.';
      if (err.response) {
        errorMessage = err.response.data.message || 'Login failed. Please check your credentials.';
      } else if (err.request) {
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        errorMessage = err.message || 'An unexpected error occurred.';
      }
      setError(errorMessage);
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        background:
          'radial-gradient(900px 480px at 15% 10%, rgba(201,162,77,0.28), transparent 55%), radial-gradient(700px 420px at 90% 80%, rgba(30,58,95,0.35), transparent 50%), linear-gradient(145deg, #0b1220 0%, #152238 45%, #0f172a 100%)',
      }}
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: '100%', borderRadius: 2 }}>
          {error}
        </Alert>
      </Snackbar>

      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 420,
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          border: '1px solid rgba(240,217,160,0.28)',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, #f8fafc 100%)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: 'auto',
              mb: 1.5,
              borderRadius: 2.5,
              display: 'grid',
              placeItems: 'center',
              background: 'linear-gradient(145deg, #f0d9a0, #c9a24d)',
              color: '#1a1408',
              boxShadow: '0 10px 24px rgba(201,162,77,0.4)',
            }}
          >
            <DiamondIcon />
          </Box>
          <Typography
            sx={{
              fontWeight: 850,
              fontSize: 28,
              letterSpacing: '0.18em',
              color: '#0f172a',
            }}
          >
            GIRVI
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: 13.5, color: '#64748b' }}>
            Sign in to your pledge workspace
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleLogin}>
          <TextField
            required
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                bgcolor: '#f8fafc',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#c9a24d',
                  borderWidth: 1.5,
                },
              },
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || !password}
            sx={{
              py: 1.35,
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 750,
              fontSize: 15,
              background: 'linear-gradient(120deg, #0f172a 0%, #1e3a5f 100%)',
              boxShadow: '0 10px 24px rgba(15,23,42,0.28)',
              '&:hover': {
                background: 'linear-gradient(120deg, #152238 0%, #254a75 100%)',
              },
            }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </Box>

        <Typography sx={{ mt: 3, textAlign: 'center', fontSize: 11, color: '#94a3b8', letterSpacing: '0.06em' }}>
          SECURE PLEDGE · LEDGER · RELEASE
        </Typography>
      </Paper>
    </Box>
  );
};

export default Login;
